import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Button, Drawer, Tabs } from "antd";
import SprTemplateNaznCard, {
  TEMPLATE_NAZN_TAB_ITEMS,
  TEMPLATE_NAZN_TAB_KEYS
} from "./spr-template-nazn-card";
import "./spr-template-nazn-drawer.css";

const DRAWER_WIDTH_KEY = "planOnline.sprTemplateNazn.cardDrawer.width";
const MIN_WIDTH = 480;
const MAX_WIDTH = 900;
const DEFAULT_WIDTH = 560;
const SCREEN_MARGIN = 100;

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

/**
 * Карточка шаблона назначений (вкладки Основная / Состав).
 */
const SprTemplateNaznDrawer = ({
  open,
  idShb,
  isNew = false,
  canEdit,
  onClose,
  onSaved
}) => {
  const [size, setSize] = useState(loadWidth);
  const [activeTab, setActiveTab] = useState(TEMPLATE_NAZN_TAB_KEYS.MAIN);

  useEffect(() => {
    if (open) setActiveTab(TEMPLATE_NAZN_TAB_KEYS.MAIN);
  }, [open, idShb, isNew]);

  useEffect(() => {
    if (isNew && activeTab !== TEMPLATE_NAZN_TAB_KEYS.MAIN) {
      setActiveTab(TEMPLATE_NAZN_TAB_KEYS.MAIN);
    }
  }, [isNew, activeTab]);

  useEffect(() => {
    if (!open) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const drawerMaxSize = maxAllowedWidth();

  const tabItems = useMemo(() => {
    if (isNew) {
      return TEMPLATE_NAZN_TAB_ITEMS.filter((item) => item.key === TEMPLATE_NAZN_TAB_KEYS.MAIN);
    }
    return TEMPLATE_NAZN_TAB_ITEMS;
  }, [isNew]);

  const onTabChange = useCallback(
    (key) => {
      if (isNew && key !== TEMPLATE_NAZN_TAB_KEYS.MAIN) return;
      setActiveTab(key);
    },
    [isNew]
  );

  const title = (
    <div className="spr-template-nazn-drawer__title">
      <Tabs
        className="spr-template-nazn-drawer__tabs"
        size="small"
        activeKey={activeTab}
        onChange={onTabChange}
        items={tabItems}
      />
    </div>
  );

  return (
    <Drawer
      placement="right"
      title={title}
      size={size}
      maxSize={drawerMaxSize}
      open={open}
      onClose={onClose}
      destroyOnHidden
      closable={false}
      rootClassName="spr-template-nazn-drawer"
      extra={
        <Button
          type="text"
          className="spr-template-nazn-drawer__close"
          icon={<CloseOutlined />}
          aria-label="Закрыть"
          title="Закрыть"
          onClick={onClose}
        />
      }
      styles={{
        body: { display: "flex", flexDirection: "column", paddingTop: 12 }
      }}
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {open && (idShb != null || isNew) ? (
        <SprTemplateNaznCard
          idShb={idShb}
          isNew={isNew}
          open={open}
          activeTab={activeTab}
          canEdit={canEdit}
          onSaved={onSaved}
          onCancel={onClose}
        />
      ) : null}
    </Drawer>
  );
};

export default SprTemplateNaznDrawer;
